/**
 * Learning Hubs List - v11.0.0
 *
 * Redesigned UI:
 * - Direct Edit + Delete icon buttons on each card (no hidden menu)
 * - Delete confirmation dialog with hub-only vs hub+PDFs option
 * - Unified Create/Edit form dialog
 * - Cleaner card layout: color-top-bar, PDF chips, progress
 * - Skeleton loading state
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Paper,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tooltip,
  CircularProgress,
  Skeleton,
  Divider,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FolderOpen as FolderIcon,
  ArrowBack as BackIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import learningHubService from '../services/learningHubService';
import libraryService from '../services/libraryService';

// ── Constants ─────────────────────────────────────────────────────────────────
const HUB_ICONS = ['📚', '🎓', '🔬', '📖', '💡', '🎯', '🚀', '⚡', '🌟', '🧠', '✏️', '🔭', '🎨', '🏆', '📝'];
const HUB_COLORS = ['#6366f1', '#f43f5e', '#f59e0b', '#10b981', '#0ea5e9', '#8b5cf6', '#f97316', '#14b8a6', '#ec4899', '#84cc16'];

const EMPTY_FORM = { name: '', description: '', icon: '📚', color: '#6366f1', pdfIds: [] };

const SAMPLE_HUBS = [
  {
    id: 'sample-geometry',
    name: 'Geometry Fundamentals',
    description: 'Learn coordinate geometry with sample materials',
    icon: '📐',
    color: '#6366f1',
    pdfIds: ['sample-coordinate-geometry'],
    isSample: true,
    pdfCount: 1,
    lastAccessed: Date.now(),
  },
  {
    id: 'sample-history',
    name: 'Indian History',
    description: 'Explore freedom movement in Hyderabad',
    icon: '📜',
    color: '#f59e0b',
    pdfIds: ['sample-freedom-movement'],
    isSample: true,
    pdfCount: 1,
    lastAccessed: Date.now(),
  },
];

// ── Loading skeleton ──────────────────────────────────────────────────────────
function HubSkeleton() {
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Skeleton variant="rectangular" height={4} />
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Skeleton variant="rounded" width={44} height={44} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="70%" height={20} />
            <Skeleton width="90%" height={16} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
        <Skeleton width="100%" height={1} sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <Skeleton variant="rounded" width={60} height={22} />
          <Skeleton variant="rounded" width={72} height={22} />
        </Box>
        <Skeleton width="100%" height={6} sx={{ mt: 1.5, borderRadius: 1 }} />
      </CardContent>
    </Card>
  );
}

// ── Hub card ──────────────────────────────────────────────────────────────────
function HubCard({ hub, allPdfs, onOpen, onEdit, onDelete }) {
  const hubPdfs = allPdfs.filter(p => (hub.pdfIds || []).includes(p.id));
  const totalPages = hubPdfs.reduce((s, p) => s + (p.totalPages || 0), 0);
  const avgProgress = hubPdfs.length > 0
    ? Math.round(hubPdfs.reduce((s, p) => s + (p.progress || 0), 0) / hubPdfs.length)
    : 0;
  const pdfCount = hub.isSample ? (hub.pdfCount || 1) : hubPdfs.length;

  const visiblePdfs = hubPdfs.slice(0, 2);
  const extraCount = hubPdfs.length - visiblePdfs.length;

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.18s ease',
        '&:hover': {
          borderColor: hub.color,
          boxShadow: `0 6px 24px ${hub.color}22`,
          transform: 'translateY(-2px)',
        },
      }}
      onClick={onOpen}
    >
      {/* Color bar */}
      <Box sx={{ height: 4, bgcolor: hub.color, flexShrink: 0 }} />

      <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header row: icon + name + action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.25 }}>
          <Box
            sx={{
              width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
              bgcolor: `${hub.color}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.375rem',
            }}
          >
            {hub.icon || '📚'}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              fontWeight={700}
              sx={{
                fontSize: '0.9375rem', lineHeight: 1.3,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            >
              {hub.name}
            </Typography>
            {hub.description && (
              <Typography
                variant="body2" color="text.secondary"
                sx={{
                  fontSize: '0.8rem', lineHeight: 1.4, mt: 0.25,
                  display: '-webkit-box', WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}
              >
                {hub.description}
              </Typography>
            )}
          </Box>

          {/* Direct Edit + Delete buttons — only for real hubs */}
          {!hub.isSample && (
            <Box sx={{ display: 'flex', flexShrink: 0, ml: 0.25 }}>
              <Tooltip title="Edit hub" placement="top">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onEdit(); }}
                  sx={{
                    color: 'text.disabled', p: 0.5,
                    '&:hover': { color: 'primary.main', bgcolor: 'primary.50' },
                  }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete hub" placement="top">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onDelete(); }}
                  sx={{
                    color: 'text.disabled', p: 0.5,
                    '&:hover': { color: 'error.main', bgcolor: 'error.50' },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Divider */}
        <Divider sx={{ mb: 1.25 }} />

        {/* Stats row */}
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
          <Chip
            label={`${pdfCount} ${pdfCount === 1 ? 'PDF' : 'PDFs'}`}
            size="small"
            sx={{
              bgcolor: `${hub.color}14`, color: hub.color,
              fontWeight: 700, fontSize: '0.7rem', height: 22,
              '& .MuiChip-label': { px: 1 },
            }}
          />
          {totalPages > 0 && (
            <Chip
              label={`${totalPages >= 1000 ? `${(totalPages / 1000).toFixed(1)}k` : totalPages} pages`}
              size="small"
              sx={{
                bgcolor: 'action.hover', fontSize: '0.7rem', height: 22,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
          {hub.isSample && (
            <Chip
              label="Sample"
              size="small"
              sx={{
                bgcolor: `${hub.color}14`, color: hub.color,
                fontWeight: 600, fontSize: '0.7rem', height: 22,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
        </Box>

        {/* Progress bar */}
        {pdfCount > 0 && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.675rem' }}>
                Progress
              </Typography>
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.675rem', color: hub.color }}>
                {avgProgress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={avgProgress}
              sx={{
                height: 5, borderRadius: 3,
                bgcolor: `${hub.color}1a`,
                '& .MuiLinearProgress-bar': { bgcolor: hub.color, borderRadius: 3 },
              }}
            />
          </Box>
        )}

        {/* PDF name chips */}
        {visiblePdfs.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 'auto', pt: 0.5 }}>
            {visiblePdfs.map(pdf => (
              <Chip
                key={pdf.id}
                label={pdf.name.length > 20 ? pdf.name.slice(0, 20) + '…' : pdf.name}
                size="small"
                sx={{
                  fontSize: '0.65rem', height: 20,
                  bgcolor: 'background.default',
                  border: '1px solid', borderColor: 'divider',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            ))}
            {extraCount > 0 && (
              <Chip
                label={`+${extraCount} more`}
                size="small"
                sx={{
                  fontSize: '0.65rem', height: 20,
                  bgcolor: `${hub.color}10`, color: hub.color,
                  fontWeight: 600,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ── Hub form dialog (create + edit) ───────────────────────────────────────────
function HubFormDialog({ open, mode, hubForm, setHubForm, allPdfs, onClose, onSubmit }) {
  const isEdit = mode === 'edit';
  const hubPdfs = allPdfs.filter(p => hubForm.pdfIds.includes(p.id));
  const otherPdfs = allPdfs.filter(p => !hubForm.pdfIds.includes(p.id));

  const togglePdf = (pdfId) => {
    setHubForm(prev => ({
      ...prev,
      pdfIds: prev.pdfIds.includes(pdfId)
        ? prev.pdfIds.filter(id => id !== pdfId)
        : [...prev.pdfIds, pdfId],
    }));
  };

  const removePdf = (pdfId) => {
    setHubForm(prev => ({ ...prev, pdfIds: prev.pdfIds.filter(id => id !== pdfId) }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ '& .MuiDialog-paper': { m: { xs: 1, sm: 2 }, maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: '1.1rem' }}>
        {isEdit ? 'Edit Learning Hub' : 'Create New Learning Hub'}
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {/* Name */}
        <TextField
          autoFocus
          label="Hub Name"
          fullWidth
          value={hubForm.name}
          onChange={(e) => setHubForm({ ...hubForm, name: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
          sx={{ mb: 2 }}
          placeholder="e.g., Physics – Class 12"
          size="small"
        />

        {/* Description */}
        <TextField
          label="Description (optional)"
          fullWidth
          multiline
          rows={2}
          value={hubForm.description}
          onChange={(e) => setHubForm({ ...hubForm, description: e.target.value })}
          sx={{ mb: 2.5 }}
          placeholder="What will you study in this hub?"
          size="small"
        />

        {/* Icon */}
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Icon
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, mb: 2.5, flexWrap: 'wrap' }}>
          {HUB_ICONS.map(icon => (
            <IconButton
              key={icon}
              onClick={() => setHubForm({ ...hubForm, icon })}
              sx={{
                border: '1.5px solid',
                borderColor: hubForm.icon === icon ? 'primary.main' : 'divider',
                bgcolor: hubForm.icon === icon ? 'primary.50' : 'transparent',
                fontSize: '1.25rem', width: 40, height: 40, borderRadius: 1.5,
              }}
            >
              {icon}
            </IconButton>
          ))}
        </Box>

        {/* Color */}
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Color
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, mb: 2.5, flexWrap: 'wrap' }}>
          {HUB_COLORS.map(color => (
            <Tooltip key={color} title={color} placement="top">
              <IconButton
                onClick={() => setHubForm({ ...hubForm, color })}
                sx={{
                  bgcolor: color, width: 34, height: 34, borderRadius: '50%',
                  outline: hubForm.color === color ? `3px solid ${color}` : 'none',
                  outlineOffset: 2,
                  '&:hover': { bgcolor: color, opacity: 0.85 },
                }}
              >
                {hubForm.color === color && (
                  <Box sx={{ color: '#fff', fontSize: '0.875rem', fontWeight: 700, lineHeight: 1 }}>✓</Box>
                )}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        {/* PDFs in hub */}
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          PDFs in this hub
          {hubForm.pdfIds.length > 0 && (
            <Box component="span" sx={{ ml: 1, color: 'primary.main', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}>
              {hubForm.pdfIds.length} selected
            </Box>
          )}
        </Typography>

        {hubPdfs.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
            {hubPdfs.map(pdf => (
              <Chip
                key={pdf.id}
                label={pdf.name.length > 30 ? pdf.name.slice(0, 30) + '…' : pdf.name}
                size="small"
                onDelete={() => removePdf(pdf.id)}
                sx={{
                  bgcolor: `${hubForm.color}10`,
                  border: `1px solid ${hubForm.color}40`,
                  color: 'text.primary',
                  '& .MuiChip-deleteIcon': { color: hubForm.color },
                  fontSize: '0.75rem',
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled" sx={{ mb: 1.5, fontStyle: 'italic' }}>
            No PDFs added yet
          </Typography>
        )}

        {/* Add PDFs from library */}
        {otherPdfs.length > 0 && (
          <>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Add from Library
            </Typography>
            <Paper
              variant="outlined"
              sx={{ maxHeight: 180, overflow: 'auto', borderRadius: 1.5 }}
            >
              <List dense disablePadding>
                {otherPdfs.map((pdf, idx) => (
                  <React.Fragment key={pdf.id}>
                    {idx > 0 && <Divider component="li" />}
                    <ListItem
                      button
                      onClick={() => togglePdf(pdf.id)}
                      sx={{ py: 0.75, px: 1.5 }}
                    >
                      <IconButton size="small" sx={{ pointerEvents: 'none', mr: 0.5 }}>
                        {hubForm.pdfIds.includes(pdf.id)
                          ? <CheckBoxIcon color="primary" sx={{ fontSize: 18 }} />
                          : <CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                      <ListItemText
                        primary={pdf.name}
                        secondary={`${pdf.totalPages || pdf.pageCount || '?'} pages`}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </>
        )}

        {allPdfs.length === 0 && (
          <Alert severity="info" sx={{ mt: 1, fontSize: '0.8rem' }}>
            No PDFs in your library yet. Upload PDFs first, then add them to a hub.
          </Alert>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={!hubForm.name.trim()}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
        >
          {isEdit ? 'Save Changes' : 'Create Hub'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Delete confirmation dialog ────────────────────────────────────────────────
function DeleteHubDialog({ open, hub, pdfCount, deleteOption, setDeleteOption, loading, onClose, onConfirm }) {
  if (!hub) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, fontWeight: 700, fontSize: '1.05rem' }}>
        <WarningIcon color="error" fontSize="small" />
        Delete "{hub.name}"?
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This hub contains <strong>{pdfCount} PDF{pdfCount !== 1 ? 's' : ''}</strong>. Choose what to do with them:
        </Typography>
        <RadioGroup value={deleteOption} onChange={e => setDeleteOption(e.target.value)}>
          <FormControlLabel
            value="hub-only"
            control={<Radio size="small" />}
            sx={{ alignItems: 'flex-start', mb: 1, '& .MuiRadio-root': { pt: 0.25 } }}
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>Remove hub only</Typography>
                <Typography variant="caption" color="text.secondary">
                  Keep all PDFs in your library — just remove this hub
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="hub-and-pdfs"
            control={<Radio size="small" color="error" />}
            sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.25 } }}
            label={
              <Box>
                <Typography variant="body2" fontWeight={600} color="error.main">
                  Remove hub and delete all PDFs
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Permanently deletes all {pdfCount} PDF{pdfCount !== 1 ? 's' : ''} from your library
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {loading ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function LearningHubsList({ onBack, onOpenHub, user, subscription, onUpgrade, driveConnected }) {
  const [hubs, setHubs] = useState([]);
  const [allPdfs, setAllPdfs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form dialog (create + edit unified)
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
  const [editingHub, setEditingHub] = useState(null);
  const [hubForm, setHubForm] = useState(EMPTY_FORM);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [hubToDelete, setHubToDelete] = useState(null);
  const [deleteOption, setDeleteOption] = useState('hub-only');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const isFreeUser = subscription && !subscription.isPaid;

  useEffect(() => {
    loadData();
    fixPageCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription, driveConnected]);

  const fixPageCounts = async () => {
    try {
      const result = await libraryService.fixMissingPageCounts();
      if (result.success && result.fixed > 0) loadData();
    } catch { /* silent */ }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [loadedHubs, loadedPdfs] = await Promise.all([
        learningHubService.getAllLearningHubs(user?.uid),
        libraryService.getAllLibraryItems(),
      ]);
      setHubs(isFreeUser ? SAMPLE_HUBS : loadedHubs);
      setAllPdfs(loadedPdfs);
    } catch (error) {
      console.error('❌ Failed to load hubs:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Form handlers ──
  const openCreate = () => {
    if (isFreeUser) { onUpgrade(); return; }
    setFormMode('create');
    setEditingHub(null);
    setHubForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (hub) => {
    setFormMode('edit');
    setEditingHub(hub);
    setHubForm({
      name: hub.name,
      description: hub.description || '',
      icon: hub.icon || '📚',
      color: hub.color || '#6366f1',
      pdfIds: hub.pdfIds || [],
    });
    setFormOpen(true);
  };

  const handleFormSubmit = async () => {
    if (!hubForm.name.trim()) return;
    try {
      if (formMode === 'create') {
        await learningHubService.createLearningHub({ ...hubForm, userId: user?.uid });
      } else {
        await learningHubService.updateLearningHub(editingHub.id, hubForm);
      }
      setFormOpen(false);
      loadData();
    } catch (error) {
      console.error('❌ Failed to save hub:', error);
    }
  };

  // ── Delete handlers ──
  const openDelete = (hub) => {
    setHubToDelete(hub);
    setDeleteOption('hub-only');
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!hubToDelete) return;
    setDeleteLoading(true);
    try {
      if (deleteOption === 'hub-and-pdfs') {
        const pdfsToDelete = allPdfs.filter(p => (hubToDelete.pdfIds || []).includes(p.id));
        await Promise.all(pdfsToDelete.map(p => libraryService.removePDFFromLibrary(p.id)));
      }
      await learningHubService.deleteLearningHub(hubToDelete.id);
      setDeleteOpen(false);
      setHubToDelete(null);
      loadData();
    } catch (error) {
      console.error('❌ Failed to delete hub:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Render ──
  const pdfCountForDelete = hubToDelete
    ? allPdfs.filter(p => (hubToDelete.pdfIds || []).includes(p.id)).length
    : 0;

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: 'background.default' }}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>

        {/* ── Header ── */}
        <Box sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          mb: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton onClick={onBack} size="small" sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
              <BackIcon fontSize="small" />
            </IconButton>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem' }, letterSpacing: '-0.3px' }}>
                  {isFreeUser ? 'Sample Hubs' : 'My Learning Hubs'}
                </Typography>
                {!loading && hubs.length > 0 && (
                  <Chip
                    label={`${hubs.length} ${hubs.length === 1 ? 'Hub' : 'Hubs'}`}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22 }}
                  />
                )}
                {isFreeUser && (
                  <Chip label="Free Preview" size="small" color="primary" sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.25 }}>
                {isFreeUser
                  ? 'Explore sample hubs — upgrade to create your own'
                  : 'Organize your PDFs into focused Learning Hubs'}
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{
              borderRadius: 2, px: 2.5, py: 1,
              textTransform: 'none', fontWeight: 700,
              fontSize: '0.875rem',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {isFreeUser ? 'Upgrade to Create Hubs' : 'New Hub'}
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* ── Loading skeletons ── */}
        {loading && (
          <Grid container spacing={{ xs: 2, sm: 2.5 }}>
            {[0, 1, 2, 3].map(i => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <HubSkeleton />
              </Grid>
            ))}
          </Grid>
        )}

        {/* ── Empty state ── */}
        {!loading && hubs.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 5, sm: 10 },
              textAlign: 'center',
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 3,
              bgcolor: 'background.paper',
            }}
          >
            <FolderIcon sx={{ fontSize: { xs: 56, sm: 72 }, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" fontWeight={700} gutterBottom>
              No Learning Hubs Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first hub to organize your study materials
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
            >
              Create Your First Hub
            </Button>
          </Paper>
        )}

        {/* ── Hub grid ── */}
        {!loading && hubs.length > 0 && (
          <Grid container spacing={{ xs: 2, sm: 2.5 }}>
            {hubs.map(hub => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={hub.id}>
                <HubCard
                  hub={hub}
                  allPdfs={allPdfs}
                  onOpen={() => onOpenHub(hub)}
                  onEdit={() => openEdit(hub)}
                  onDelete={() => openDelete(hub)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* ── Form dialog (create + edit) ── */}
      <HubFormDialog
        open={formOpen}
        mode={formMode}
        hubForm={hubForm}
        setHubForm={setHubForm}
        allPdfs={allPdfs}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
      />

      {/* ── Delete confirmation dialog ── */}
      <DeleteHubDialog
        open={deleteOpen}
        hub={hubToDelete}
        pdfCount={pdfCountForDelete}
        deleteOption={deleteOption}
        setDeleteOption={setDeleteOption}
        loading={deleteLoading}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
}

export default LearningHubsList;
